/**
 * Shared input mapping for the Sniper auto-NO tail's "No Size" field
 * (`noSize?: number | null`).
 *
 * The auto-NO sweep normally fires at the SAME contract size as the market's
 * main sweep (`size`). `noSize` lets an operator size the auto-NO leg
 * independently — the field is nullable: `null` (or absent) means "inherit
 * the main `size`" (resolved by the Rust engine), NOT zero and NOT some
 * hardcoded default. An EMPTY input therefore maps to an explicit `null`
 * (inherit), NEVER `0`; a set value must be a positive number of contracts —
 * mirrors `size`'s own `>= 1` bound (not integer-only, unlike
 * `counterStuckSeconds`'s whole-seconds rule).
 */

export const NO_SIZE_LABEL = 'No Size';

export const NO_SIZE_HELP =
  'Auto-NO sweep size (contracts) — independent of the main Size. Empty inherits the main Size.';

export const NO_SIZE_ERROR = 'Must be a positive number';

export type ParsedNoSize =
  | { ok: true; value: number | null }
  | { ok: false; error: string };

/** Raw input string → DTO field. Empty = explicit `null` (inherit `size`). */
export function parseNoSizeInput(raw: string): ParsedNoSize {
  const trimmed = raw.trim();
  if (trimmed === '') return { ok: true, value: null };
  const n = Number(trimmed);
  if (!Number.isFinite(n) || n < 1) {
    return { ok: false, error: NO_SIZE_ERROR };
  }
  return { ok: true, value: n };
}

/** Market/patch field → input string (`null`/absent renders as empty). */
export function noSizeToInput(value: number | null | undefined): string {
  return value != null ? String(value) : '';
}
